import { withFullContent, type WorkspaceFile } from './workspace-files';
import { extractUrl } from './artifact-image';
import { fetchRemoteDocument } from './remote-document';
import { effectiveContent, isContentPreview, parseFileRecordContent } from './file-record';

// Open / download / metadata helpers for a workspace artifact, shared by the Files gallery card and the
// file-detail page. Web analogs of the native `FileDetailAction` (open in the OS viewer / OS share
// sheet), except the "share" action is a straight download here — orion-web is web-only, so there is no
// OS share sheet to hand off to. A link artifact (content that is/embeds an https URL) opens/downloads
// the URL; inline data becomes a typed Blob.

// Text-ish MIME types carry `;charset=utf-8` — a `blob:`-URL's text content is decoded per the MIME
// type's charset param, defaulting to Latin-1/Windows-1252 when it's absent, which mangles multi-byte
// UTF-8 (e.g. Hebrew) into mojibake. Binary types (images, PDF) have no charset to declare.
const MIME_BY_EXT: Record<string, string> = {
  txt: 'text/plain;charset=utf-8',
  md: 'text/markdown;charset=utf-8',
  csv: 'text/csv;charset=utf-8',
  json: 'application/json;charset=utf-8',
  html: 'text/html;charset=utf-8',
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  // Video, mirroring the harness's own map (attach-file-tool). Without these a clip falls through
  // to the `text/plain` default below and opens as a wall of binary instead of playing.
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  '3gp': 'video/3gpp',
};

/** A body is declared (open/share/preview/size offered) iff the file has inline content or points at
 *  bytes stored elsewhere. Apps carry neither. */
export function hasContent(item: WorkspaceFile): boolean {
  return !item.isApp && (!!item.content?.trim() || !!item.referenceUrl?.trim());
}

/** The artifact's extension: the explicit `extension`, else parsed off the name. */
export function extensionOf(item: WorkspaceFile): string {
  if (item.extension && item.extension.trim()) return item.extension.trim().toLowerCase();
  const dot = item.name.lastIndexOf('.');
  return dot > 0 && dot < item.name.length - 1 ? item.name.slice(dot + 1).toLowerCase() : '';
}

/** A download-safe filename: the name, with the extension appended when it isn't already in the name. */
export function downloadName(item: WorkspaceFile): string {
  const ext = extensionOf(item);
  return item.name.includes('.') || !ext ? item.name : `${item.name}.${ext}`;
}

/** The name to SHOW for a file: its last path segment, with the extension `downloadName` would add. A
 *  `WorkspaceFile.name` is the whole key under the workspace (`reports/attendance-trend.csv`), so printing it
 *  raw put the folder in front of every name inside a folder — where the breadcrumb already says it. */
export function displayName(item: WorkspaceFile): string {
  const full = downloadName(item);
  const cut = full.lastIndexOf('/');
  return cut === -1 ? full : full.slice(cut + 1);
}

/** A friendly file-type label from the extension ("Markdown", "Text", else uppercased ext). null if none.
 *  Mirrors native `typeLabel`. */
export function typeLabel(ext: string): string | null {
  if (!ext) return null;
  if (ext === 'md' || ext === 'markdown') return 'Markdown';
  if (ext === 'txt') return 'Text';
  return ext.toUpperCase();
}

/** The inline content byte size (UTF-8), or null when the content is a bare URL (remote — size unknown
 *  without fetching, so the detail hides the Size row for link artifacts). A FileRecord header's
 *  `sizeBytes` (#7133 Phase 2) is authoritative when present — it's the real upload size, not the length
 *  of whatever string the artifact happens to be stored as (a pointer file's body is just its URL). */
export function inlineByteSize(item: WorkspaceFile): number | null {
  if (!hasContent(item)) return null;
  const { header, body } = parseFileRecordContent(effectiveContent(item));
  if (header?.sizeBytes !== undefined) return header.sizeBytes;
  // A listing row's body is only its head, so measuring it would size every large file at ~2 KB. The
  // stored size is the server's own count of the whole body.
  const stored = Number(item.sizeBytes);
  if (isContentPreview(item) && Number.isFinite(stored) && stored > 0) return stored;
  if (extractUrl(body) && body.trim() === extractUrl(body)) return null; // bare URL, remote
  return new Blob([body]).size;
}

/** Human-readable byte size ("512 B", "12 KB", "3.4 MB") — binary units, one decimal below 10. Mirrors
 *  native `humanSize`. */
export function humanSize(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  if (unit === 0) return `${Math.round(value)} B`;
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** A FileRecord header's `mimeType` (#7133 Phase 2) is authoritative when present; else infer from the
 *  extension as before. A text-ish MIME type with no charset param gets `;charset=utf-8` appended so the
 *  browser doesn't fall back to Latin-1 when decoding the blob (mangling non-ASCII content) — never
 *  double-appended when the header already carries one. */
export function mimeFor(item: WorkspaceFile): string {
  const { header } = parseFileRecordContent(effectiveContent(item));
  const mime = header?.mimeType || MIME_BY_EXT[extensionOf(item)] || 'text/plain;charset=utf-8';
  return withUtf8Charset(mime);
}

/** Append `;charset=utf-8` to a text-ish MIME type that doesn't already declare a charset. Binary types
 *  (images, PDF, …) are left untouched — a charset param on them is meaningless. */
function withUtf8Charset(mime: string): string {
  if (/;\s*charset=/i.test(mime)) return mime;
  const isTextish = mime.startsWith('text/') || mime === 'application/json' || mime === 'image/svg+xml';
  return isTextish ? `${mime};charset=utf-8` : mime;
}

/** Open the artifact: a link artifact opens its URL in a new tab; inline data opens as a typed Blob.
 *
 *  A listing row may carry only the head of its body (`contentTruncated`). The tab is then opened NOW,
 *  inside the click's user gesture, and pointed at the file once the whole body has been read — opening
 *  it after the read would land outside the gesture and the popup blocker would eat it. */
export function openArtifact(item: WorkspaceFile): void {
  if (!hasContent(item)) return;
  if (isContentPreview(item)) {
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    withFullContent(item).then(
      (full) => {
        if (tab) tab.location.href = artifactHref(full);
      },
      () => tab?.close(),
    );
    return;
  }
  window.open(artifactHref(item), '_blank', 'noopener,noreferrer');
}

/** Where `openArtifact` points a tab: the link artifact's URL, else a typed Blob of the inline body. */
function artifactHref(item: WorkspaceFile): string {
  const { body } = parseFileRecordContent(effectiveContent(item));
  const url = extractUrl(body);
  if (url) return url;
  const blobUrl = URL.createObjectURL(new Blob([body], { type: mimeFor(item) }));
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return blobUrl;
}

/** Download the artifact — and for a file whose bytes live elsewhere, that means **fetching them and
 *  saving them**, not navigating to them.
 *
 *  A remote file used to be `window.open(url)`. That is not a download: WixMP serves a PDF with an inline
 *  disposition, so the browser rendered it in a new tab and nothing was ever saved — which is exactly the
 *  reported bug (Download appears to do nothing on a chat PDF while Open-in-new-tab works, because the
 *  latter is an honest anchor and the former was a mislabelled one). It could also be swallowed outright by
 *  a popup blocker, leaving no feedback at all.
 *
 *  So: fetch the bytes, wrap them in a Blob, and click a same-origin `download` anchor — the same mechanism
 *  the inline path already used, and the same one `downloadRemoteFile` uses for a wixel preview image. The
 *  cross-origin `download` attribute a bare `<a href={signedUrl} download>` would carry is ignored by the
 *  browser, which is why the fetch is needed rather than just an attribute.
 *
 *  A fetch the media host REFUSES the browser (no `Access-Control-Allow-Origin`) is retried through the pod
 *  by `fetchRemoteDocument` — that is not an edge case: `links.wixel.com/link/<id>/raw`, how every
 *  Wixel-generated document is attached, answers a 302 carrying no CORS headers, so a Wixel PDF used to hit
 *  the fallback below on every single attempt. Opening the URL is kept as the last resort it always should
 *  have been, and it is a poor one: it fires from an async `catch`, after the click's user-gesture window
 *  has closed, so the popup blocker eats it and Download appears to do nothing at all. That was the report.
 *
 *  orion-web is web-only, so this always downloads directly rather than going through `navigator.share` —
 *  that API's mobile-browser support is inconsistent and native has its own true OS share sheet (see this
 *  repo's CLAUDE.md three-surface-parity note: "pack download is a web file download AND an iOS/Android
 *  share-sheet export — one feature, three idioms"). */
export async function downloadArtifact(row: WorkspaceFile): Promise<void> {
  if (!hasContent(row)) return;
  // A listing row may carry only the head of its body; the saved file must be the whole of it.
  const item = await withFullContent(row);
  const { body } = parseFileRecordContent(effectiveContent(item));
  const url = extractUrl(body);
  if (url && body.trim() === url) {
    await downloadRemoteFile(url, downloadName(item));
    return;
  }
  saveBlob(new File([body], downloadName(item), { type: mimeFor(item) }), downloadName(item));
}

/** Downloads a REMOTE url as `filename` — fetch-as-blob so the browser SAVES it rather than merely
 *  navigating to it, via `fetchRemoteDocument` so a host that refuses the browser CORS is re-fetched by the
 *  pod instead of falling straight through to the window-open last resort. The wixel preview
 *  image is addressed by URL alone rather than by a `WorkspaceFile`, so it comes through here instead of
 *  `downloadArtifact`; both share one save mechanism so "Download" cannot mean two different things. */
export async function downloadRemoteFile(url: string, filename: string): Promise<void> {
  try {
    const res = await fetchRemoteDocument(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    saveBlob(await res.blob(), filename);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Saves `blob` to disk under `filename`, via a transient same-origin `download` anchor. */
function saveBlob(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
